
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import API_URL from "../config";



const GestionarEtiquetasDocentes = () => {
  const [etiquetas, setEtiquetas] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [nuevoColor, setNuevoColor] = useState("#2563eb");
  const [loading, setLoading] = useState(true);

  // FILTROS
  const [filtroNombre, setFiltroNombre] = useState("");
  // Filtro de etiquetas multi-selección: array de "sin" y/o ids de etiqueta
  // (como texto). Vacío = sin filtrar, muestra todos.
  const [filtroEtiquetas, setFiltroEtiquetas] = useState([]);
  const [menuEtiquetasAbierto, setMenuEtiquetasAbierto] = useState(false);
  const menuEtiquetasRef = useRef(null);
  const [filtroGuia, setFiltroGuia] = useState("todos"); // "todos" | "guia" | "libre"

  // Cierra el desplegable de etiquetas al hacer clic fuera de él
  useEffect(() => {
    const alHacerClickFuera = (e) => {
      if (menuEtiquetasRef.current && !menuEtiquetasRef.current.contains(e.target)) {
        setMenuEtiquetasAbierto(false);
      }
    };
    document.addEventListener("mousedown", alHacerClickFuera);
    return () => document.removeEventListener("mousedown", alHacerClickFuera);
  }, []);

  const toggleFiltroEtiqueta = (valor) => {
    setFiltroEtiquetas((prev) =>
      prev.includes(valor) ? prev.filter((v) => v !== valor) : [...prev, valor]
    );
  };

  const etiquetaFiltroLabel =
    filtroEtiquetas.length === 0
      ? "Etiqueta: todas"
      : `Etiqueta: ${filtroEtiquetas.length} seleccionada${filtroEtiquetas.length > 1 ? "s" : ""}`;

  /* =========================
     ETIQUETAS POR SERVICIO

     Qué etiquetas hacen que un profesor aparezca en cada servicio.
     CDA no usa etiquetas (usa "guía de grado"), por eso se excluye aquí.
  ========================= */
  const [servicios, setServicios] = useState([]);
  const [asignaciones, setAsignaciones] = useState({}); // { servicioId: [etiqueta_id, ...] }

  // Cargar etiquetas
  const fetchEtiquetas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/etiquetas-docentes`);
      setEtiquetas(res.data);
    } catch (err) {
      console.error("Error cargando etiquetas:", err);
    }
  };

  const fetchServiciosYAsignaciones = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/servicios`);

      // CDA no usa etiquetas — se filtra por su ruta fija
      const serviciosConEtiquetas = (res.data || []).filter(
        (s) => s.ruta !== "/citas-cda-2026"
      );
      setServicios(serviciosConEtiquetas);

      const entradas = await Promise.all(
        serviciosConEtiquetas.map(async (s) => {
          const r = await axios.get(
            `${API_URL}/api/etiquetas-docentes/servicio/${s.id}`
          );
          return [s.id, (r.data || []).map((e) => e.id)];
        })
      );

      setAsignaciones(Object.fromEntries(entradas));
    } catch (err) {
      console.error("Error cargando etiquetas por servicio:", err);
    }
  };

  // Marca/desmarca una etiqueta para un servicio y guarda de una vez
  const toggleEtiquetaServicio = async (servicioId, etiquetaId) => {
    const actuales = asignaciones[servicioId] || [];

    const nuevas = actuales.includes(etiquetaId)
      ? actuales.filter((id) => id !== etiquetaId)
      : [...actuales, etiquetaId];

    setAsignaciones({ ...asignaciones, [servicioId]: nuevas });

    await axios.post(
      `${API_URL}/api/etiquetas-docentes/servicio/${servicioId}`,
      { etiqueta_ids: nuevas }
    );
  };

  // Cargar profesores según filtro
  const fetchProfesores = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/profesores`);
      const data = res.data;

      const profesoresConEtiquetas = await Promise.all(
        data.map(async (prof) => {
          try {
            const etiquetaRes = await axios.get(
              `${API_URL}/api/etiquetas-docentes/profesor/${prof.id}`
            );
            return { ...prof, etiqueta: etiquetaRes.data };
          } catch {
            return { ...prof, etiqueta: null };
          }
        })
      );

      setProfesores(profesoresConEtiquetas);
    } catch (err) {
      console.error("Error cargando profesores:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEtiquetas();
    fetchProfesores();
    fetchServiciosYAsignaciones();
  }, []);

  // Crear nueva etiqueta
  const agregarEtiqueta = async () => {
    if (!nuevoNombre) return;
    try {
      await axios.post(`${API_URL}/api/etiquetas-docentes`, {
        nombre: nuevoNombre,
        color: nuevoColor,
      });
      setNuevoNombre("");
      setNuevoColor("#2563eb");
      fetchEtiquetas();
    } catch (err) {
      console.error("Error creando etiqueta:", err);
    }
  };

  // Editar etiqueta
  const actualizarEtiqueta = async (id, nombre, color) => {
    try {
      await axios.post(`${API_URL}/api/etiquetas-docentes/${id}`, { nombre, color });
      fetchEtiquetas();
    } catch (err) {
      console.error("Error actualizando etiqueta:", err);
    }
  };

  // Eliminar etiqueta
  const eliminarEtiqueta = async (id) => {
    if (!window.confirm("¿Seguro que quieres eliminar esta etiqueta?")) return;
    try {
      await axios.post(`${API_URL}/api/etiquetas-docentes/${id}/eliminar`);
      fetchEtiquetas();
      fetchProfesores(); // refrescar profesores en caso de que alguno tuviera esa etiqueta
    } catch (err) {
      console.error("Error eliminando etiqueta:", err);
    }
  };

  // Asignar etiqueta a un profesor
  const asignarEtiqueta = async (profesorId, etiquetaId) => {
    try {
      if (!etiquetaId) {
        // quitar etiqueta
        await axios.post(`${API_URL}/api/etiquetas-docentes/profesor/${profesorId}`, { etiqueta_id: null });
      } else {
        await axios.post(`${API_URL}/api/etiquetas-docentes/profesor/${profesorId}`, { etiqueta_id: etiquetaId });
      }
      fetchProfesores();
    } catch (err) {
      console.error("Error asignando etiqueta:", err);
    }
  };

  // Restaurar la etiqueta que tenía este profesor antes del último cambio
  const restaurarEtiquetaIndividual = async (profesorId) => {
    try {
      await axios.post(`${API_URL}/api/etiquetas-docentes/profesor/${profesorId}/restaurar`);
      fetchProfesores();
    } catch (err) {
      console.error("Error restaurando etiqueta:", err);
      alert("❌ No se pudo restaurar la etiqueta anterior");
    }
  };

  // Filtrar profesores según búsqueda y tipo de etiqueta
  const profesoresFiltrados = profesores.filter((p) => {
    const nombreMatch = p.nombre.toLowerCase().includes(filtroNombre.toLowerCase());
    const etiquetaMatch =
      filtroEtiquetas.length === 0 ||
      (p.etiqueta
        ? filtroEtiquetas.includes(String(p.etiqueta.id))
        : filtroEtiquetas.includes("sin"));
    const guiaMatch =
      filtroGuia === "todos" ||
      (filtroGuia === "guia" && p.grado_guia_id) ||
      (filtroGuia === "libre" && !p.grado_guia_id);
    return nombreMatch && etiquetaMatch && guiaMatch;
  });

  // Selección manual de docentes (checkboxes), independiente del filtro,
  // para asignar una etiqueta solo a un grupo elegido a mano.
  const [seleccionados, setSeleccionados] = useState([]);

  // Marca con el checkbox a TODOS los que están filtrados en pantalla
  // ahora mismo, para poder revisar/quitar alguno a mano antes de
  // asignarles la etiqueta (a diferencia de "Asignar a todos", que no
  // permite ajustar la lista antes de aplicar el cambio).
  const seleccionarTodosFiltrados = () => {
    setSeleccionados(profesoresFiltrados.map((p) => p.id));
  };

  const toggleSeleccionado = (profesorId) => {
    setSeleccionados((prev) =>
      prev.includes(profesorId)
        ? prev.filter((id) => id !== profesorId)
        : [...prev, profesorId]
    );
  };

  // Asignar una etiqueta a TODOS los docentes que están filtrados en
  // pantalla en ese momento (no a todos los que existen en la BD) — así
  // se puede filtrar primero "Sin etiqueta" y aplicar solo a esos, por
  // ejemplo, sin pisar la etiqueta de los que ya tienen una distinta.
  const [etiquetaMasiva, setEtiquetaMasiva] = useState("");

  const asignarEtiquetaMasiva = async () => {
    if (!etiquetaMasiva) {
      alert("⚠️ Selecciona primero qué etiqueta quieres asignar (o \"Quitar etiqueta\" / \"Restaurar\")");
      return;
    }

    const quitar = etiquetaMasiva === "__quitar__";
    const restaurar = etiquetaMasiva === "__restaurar__";
    const nombreEtiqueta = quitar || restaurar
      ? null
      : etiquetas.find((e) => e.id === Number(etiquetaMasiva))?.nombre || "";

    const confirmar = window.confirm(
      restaurar
        ? `¿Restaurar, a cada uno de los ${profesoresFiltrados.length} docentes filtrados, la etiqueta que tenía ANTES del último cambio? Cada profesor vuelve a la suya propia (no todos quedan igual).`
        : quitar
        ? `¿Quitar la etiqueta a los ${profesoresFiltrados.length} docentes que están filtrados ahora? Quedarán "Sin etiqueta".`
        : `¿Asignar la etiqueta "${nombreEtiqueta}" a los ${profesoresFiltrados.length} docentes que están filtrados ahora?\n\n` +
          `Esto REEMPLAZA la etiqueta actual de cada uno (un profesor solo puede tener una etiqueta a la vez).`
    );

    if (!confirmar) return;

    try {
      await Promise.all(
        profesoresFiltrados.map((p) =>
          restaurar
            ? axios.post(`${API_URL}/api/etiquetas-docentes/profesor/${p.id}/restaurar`)
            : axios.post(`${API_URL}/api/etiquetas-docentes/profesor/${p.id}`, {
                etiqueta_id: quitar ? null : etiquetaMasiva,
              })
        )
      );

      alert(
        restaurar
          ? "✅ Etiqueta anterior restaurada en todos los docentes filtrados"
          : quitar
          ? "✅ Etiqueta quitada a todos los docentes filtrados"
          : "✅ Etiqueta asignada a todos los docentes filtrados"
      );
      fetchProfesores();
    } catch (err) {
      console.error("Error en asignación masiva:", err);
      alert("❌ Ocurrió un error actualizando la etiqueta a varios docentes");
    }
  };

  // Asignar una etiqueta solo a los docentes marcados con el checkbox
  const [etiquetaSeleccionados, setEtiquetaSeleccionados] = useState("");

  const asignarEtiquetaSeleccionados = async () => {
    if (seleccionados.length === 0) {
      alert("⚠️ Marca al menos un docente");
      return;
    }

    if (!etiquetaSeleccionados) {
      alert("⚠️ Selecciona primero qué etiqueta quieres asignar (o \"Quitar etiqueta\" / \"Restaurar\")");
      return;
    }

    const quitar = etiquetaSeleccionados === "__quitar__";
    const restaurar = etiquetaSeleccionados === "__restaurar__";
    const nombreEtiqueta = quitar || restaurar
      ? null
      : etiquetas.find((e) => e.id === Number(etiquetaSeleccionados))?.nombre || "";

    const confirmar = window.confirm(
      restaurar
        ? `¿Restaurar, a cada uno de los ${seleccionados.length} docentes marcados, la etiqueta que tenía ANTES del último cambio? Cada profesor vuelve a la suya propia.`
        : quitar
        ? `¿Quitar la etiqueta a los ${seleccionados.length} docentes marcados? Quedarán "Sin etiqueta".`
        : `¿Asignar la etiqueta "${nombreEtiqueta}" a los ${seleccionados.length} docentes marcados?\n\n` +
          `Esto REEMPLAZA la etiqueta actual de cada uno.`
    );

    if (!confirmar) return;

    try {
      await Promise.all(
        seleccionados.map((profesorId) =>
          restaurar
            ? axios.post(`${API_URL}/api/etiquetas-docentes/profesor/${profesorId}/restaurar`)
            : axios.post(`${API_URL}/api/etiquetas-docentes/profesor/${profesorId}`, {
                etiqueta_id: quitar ? null : etiquetaSeleccionados,
              })
        )
      );

      alert(
        restaurar
          ? "✅ Etiqueta anterior restaurada en los docentes marcados"
          : quitar
          ? "✅ Etiqueta quitada a los docentes marcados"
          : "✅ Etiqueta asignada a los docentes marcados"
      );
      setSeleccionados([]);
      fetchProfesores();
    } catch (err) {
      console.error("Error asignando a seleccionados:", err);
      alert("❌ Ocurrió un error asignando la etiqueta");
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Gestionar Etiquetas Docentes</h2>

      {/* Crear nueva etiqueta */}
      <div className="flex flex-wrap gap-2 mb-6">
        <input
          type="text"
          placeholder="Nombre de la etiqueta"
          value={nuevoNombre}
          onChange={(e) => setNuevoNombre(e.target.value)}
          className="border rounded px-3 py-1 flex-1"
        />
        <input
          type="color"
          value={nuevoColor}
          onChange={(e) => setNuevoColor(e.target.value)}
          className="w-12 h-12 border rounded"
        />
        <button
          onClick={agregarEtiqueta}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Agregar
        </button>
      </div>

      {/* Lista de etiquetas */}
      <div className="overflow-x-auto mb-6">
      <table className="w-full table-auto border-collapse border">
        <thead>
          <tr className="bg-gray-200">
            <th className="border px-4 py-2">Nombre</th>
            <th className="border px-4 py-2">Color</th>
            <th className="border px-4 py-2">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {etiquetas.map((e) => (
            <tr key={e.id}>
              <td className="border px-4 py-2">
                <input
                  type="text"
                  value={e.nombre}
                  onChange={(ev) => actualizarEtiqueta(e.id, ev.target.value, e.color)}
                  className="border px-2 py-1 rounded w-full"
                />
              </td>
              <td className="border px-4 py-2">
                <input
                  type="color"
                  value={e.color}
                  onChange={(ev) => actualizarEtiqueta(e.id, e.nombre, ev.target.value)}
                  className="w-10 h-10 border rounded"
                />
              </td>
              <td className="border px-4 py-2">
                <button
                  onClick={() => eliminarEtiqueta(e.id)}
                  className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                >
                  Eliminar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* ================================================
          ETIQUETAS POR SERVICIO
          Define qué etiqueta(s) hacen que un profesor aparezca
          en cada servicio. CDA no aparece aquí (usa guía de grado).
      ================================================ */}
      <h2 className="text-xl font-bold mb-2">
        🔗 Etiquetas por servicio
      </h2>
      <p className="text-gray-600 mb-4">
        Marca qué etiquetas hacen que un profesor se muestre en cada
        servicio. Un profesor aparece si tiene CUALQUIERA de las etiquetas
        marcadas para ese servicio. CDA no se controla por etiquetas (usa
        "guía de grado" en Gestionar Grados), por eso no aparece en esta lista.
      </p>

      <div className="space-y-6 mb-8">
        {servicios.map((s) => (
          <div key={s.id} className="border rounded-xl p-4 bg-white">
            <h3 className="font-bold mb-3">{s.nombre}</h3>

            <div className="flex flex-wrap gap-3">
              {etiquetas.map((e) => {
                const activa = (asignaciones[s.id] || []).includes(e.id);
                return (
                  <label
                    key={e.id}
                    className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer"
                    style={{
                      borderColor: activa ? e.color : "#e5e7eb",
                      background: activa ? `${e.color}20` : "white",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={activa}
                      onChange={() => toggleEtiquetaServicio(s.id, e.id)}
                    />
                    <span
                      className="w-3 h-3 rounded-full inline-block"
                      style={{ background: e.color }}
                    />
                    {e.nombre}
                  </label>
                );
              })}

              {etiquetas.length === 0 && (
                <p className="text-sm text-gray-500">
                  Todavía no hay etiquetas creadas arriba.
                </p>
              )}
            </div>
          </div>
        ))}

        {servicios.length === 0 && (
          <p className="text-sm text-gray-500">Cargando servicios...</p>
        )}
      </div>

      {/* FILTROS PROFESORES */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Buscar profesor por nombre..."
          value={filtroNombre}
          onChange={(e) => setFiltroNombre(e.target.value)}
          className="border rounded px-3 py-1 flex-1"
        />
        <div className="relative" ref={menuEtiquetasRef}>
          <button
            type="button"
            onClick={() => setMenuEtiquetasAbierto((v) => !v)}
            className="border rounded px-3 py-1 bg-white text-left min-w-[190px] flex items-center justify-between gap-2"
          >
            {etiquetaFiltroLabel}
            <span className="text-gray-400">▾</span>
          </button>

          {menuEtiquetasAbierto && (
            <div className="absolute z-20 mt-1 bg-white border rounded shadow-lg p-2 min-w-[230px] max-h-72 overflow-y-auto">
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={filtroEtiquetas.includes("sin")}
                  onChange={() => toggleFiltroEtiqueta("sin")}
                />
                Sin etiqueta
              </label>

              <div className="border-t my-1" />

              {etiquetas.map((e) => (
                <label
                  key={e.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={filtroEtiquetas.includes(String(e.id))}
                    onChange={() => toggleFiltroEtiqueta(String(e.id))}
                  />
                  <span
                    className="w-3 h-3 rounded-full inline-block"
                    style={{ background: e.color }}
                  />
                  {e.nombre}
                </label>
              ))}

              {filtroEtiquetas.length > 0 && (
                <>
                  <div className="border-t my-1" />
                  <button
                    type="button"
                    onClick={() => setFiltroEtiquetas([])}
                    className="text-xs text-blue-600 underline px-2 py-1"
                  >
                    Limpiar filtro
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <select
          value={filtroGuia}
          onChange={(e) => setFiltroGuia(e.target.value)}
          className="border rounded px-3 py-1"
        >
          <option value="todos">Guía CDA: todos</option>
          <option value="libre">🟢 Libres (no son guía)</option>
          <option value="guia">🎓 Ya son guía de un grado</option>
        </select>
        <button
          onClick={seleccionarTodosFiltrados}
          className="bg-slate-700 text-white px-4 py-1 rounded hover:bg-slate-800"
        >
          ☑️ Seleccionar todo ({profesoresFiltrados.length})
        </button>
        {seleccionados.length > 0 && (
          <button
            onClick={() => setSeleccionados([])}
            className="bg-white border border-slate-400 text-slate-700 px-4 py-1 rounded hover:bg-slate-50"
          >
            ✖️ Quitar selección ({seleccionados.length})
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 -mt-2 mb-4">
        "Libres" son los docentes que no son guía de ningún grado en CDA. Filtra por nombre, por una
        etiqueta específica o por guía, dale "Seleccionar todo" y asígnalos a otra etiqueta con
        "Asignar a marcados" — así puedes quitar alguno a mano antes de aplicar el cambio.
      </p>

      {/* ASIGNACIÓN MASIVA */}
      <div className="flex flex-wrap items-center gap-3 mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
        <span className="text-sm font-semibold text-indigo-900">
          👥 Asignar a los {profesoresFiltrados.length} docentes filtrados:
        </span>

        <select
          value={etiquetaMasiva}
          onChange={(e) => setEtiquetaMasiva(e.target.value)}
          className="border rounded px-3 py-1"
        >
          <option value="">-- Elige una etiqueta --</option>
          <option value="__quitar__">🚫 Quitar etiqueta</option>
          <option value="__restaurar__">↩️ Restaurar etiqueta anterior</option>
          {etiquetas.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nombre}
            </option>
          ))}
        </select>

        <button
          onClick={asignarEtiquetaMasiva}
          className="bg-indigo-600 text-white px-4 py-1 rounded hover:bg-indigo-700"
        >
          {etiquetaMasiva === "__quitar__"
            ? "Quitar a todos"
            : etiquetaMasiva === "__restaurar__"
            ? "Restaurar a todos"
            : "Asignar a todos"}
        </button>
      </div>

      {/* ASIGNACIÓN A LOS MARCADOS CON CHECKBOX */}
      <div className="flex flex-wrap items-center gap-3 mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <span className="text-sm font-semibold text-emerald-900">
          ✅ Asignar a los {seleccionados.length} docentes marcados:
        </span>

        <select
          value={etiquetaSeleccionados}
          onChange={(e) => setEtiquetaSeleccionados(e.target.value)}
          className="border rounded px-3 py-1"
        >
          <option value="">-- Elige una etiqueta --</option>
          <option value="__quitar__">🚫 Quitar etiqueta</option>
          <option value="__restaurar__">↩️ Restaurar etiqueta anterior</option>
          {etiquetas.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nombre}
            </option>
          ))}
        </select>

        <button
          onClick={asignarEtiquetaSeleccionados}
          className="bg-emerald-600 text-white px-4 py-1 rounded hover:bg-emerald-700"
        >
          {etiquetaSeleccionados === "__quitar__"
            ? "Quitar a marcados"
            : etiquetaSeleccionados === "__restaurar__"
            ? "Restaurar a marcados"
            : "Asignar a marcados"}
        </button>

        {seleccionados.length > 0 && (
          <button
            onClick={() => setSeleccionados([])}
            className="text-sm text-gray-600 underline"
          >
            Quitar selección
          </button>
        )}
      </div>

      {/* LISTA PROFESORES */}
      {loading ? (
        <p>Cargando profesores...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {profesoresFiltrados.map((prof) => (
            <div
              key={prof.id}
              className={`p-4 border rounded-xl shadow hover:shadow-lg flex flex-col items-center relative ${
                seleccionados.includes(prof.id) ? "ring-2 ring-emerald-500" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={seleccionados.includes(prof.id)}
                onChange={() => toggleSeleccionado(prof.id)}
                className="absolute top-3 left-3 w-5 h-5"
              />

              <img
                src={prof.foto || `${API_URL}/Fotos_profesores/default.png`}
                alt={prof.nombre}
                className="w-28 h-28 rounded-full mb-2 object-cover border-2 border-gray-300"
              />
              <h4 className="font-semibold">{prof.nombre}</h4>
              <p className="text-sm mb-2">{prof.email}</p>

              {/* GUÍA DE GRADO (CDA) */}
              {prof.grado_guia_id ? (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 mb-2">
                  🎓 Guía de {prof.grado_guia}
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 mb-2">
                  🟢 Libre (sin grado guía)
                </span>
              )}

              {/* ETIQUETA ACTUAL */}
              {prof.etiqueta && (
                <span
                  className="px-3 py-1 rounded-full text-white mb-2"
                  style={{ backgroundColor: prof.etiqueta.color }}
                >
                  {prof.etiqueta.nombre}
                </span>
              )}

              {/* ETIQUETA ANTERIOR (antes del último cambio) */}
              {prof.etiqueta_anterior_id &&
                prof.etiqueta_anterior_id !== prof.etiqueta?.id && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <span>
                      🕓 Antes:{" "}
                      <span
                        className="font-semibold"
                        style={{ color: prof.etiqueta_anterior_color }}
                      >
                        {prof.etiqueta_anterior_nombre}
                      </span>
                    </span>
                    <button
                      onClick={() => restaurarEtiquetaIndividual(prof.id)}
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      ↩️ Restaurar
                    </button>
                  </div>
                )}

              {/* ASIGNAR NUEVA ETIQUETA */}
              <select
                value={prof.etiqueta?.id || ""}
                onChange={(e) =>
                  asignarEtiqueta(prof.id, e.target.value ? parseInt(e.target.value) : null)
                }
                className="border rounded px-2 py-1 w-full mb-2"
              >
                <option value="">-- Sin etiqueta --</option>
                {etiquetas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nombre}
                  </option>
                ))}
              </select>

              <button
                onClick={() =>
                  asignarEtiqueta(prof.id, prof.etiqueta?.id || null)
                }
                className="bg-green-500 text-white px-4 py-1 rounded hover:bg-green-600"
              >
                Guardar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GestionarEtiquetasDocentes;


